/**
 * FauxDS Button - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Button component for prototyping.
 * It is NOT the real @faire/slate Button and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Button, see: @faire/slate/components/button
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 7175-9213 (Slate Button)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Primary, Secondary, Tertiary, Plain
 * - All sizes verified: Small (40px), Medium (48px)
 * - All states verified: Default, Hover, Active, Disabled, Loading
 * - Destructive variant verified (uses critical colors)
 * - Icon positions verified: Start, End
 * - Loading label option verified
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

import LoadingSpinner from "./LoadingSpinner";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "plain";
export type ButtonSize = "xSmall" | "small" | "medium";
export type ButtonIconPosition = "start" | "end";

export interface ButtonProps {
  /**
   * Button content (text)
   */
  children: React.ReactNode;
  /**
   * Button variant
   * @default "primary"
   */
  variant?: ButtonVariant;
  /**
   * Whether the button is destructive (uses critical/red colors)
   * @default false
   */
  destructive?: boolean;
  /**
   * Button size
   * @default "medium"
   */
  size?: ButtonSize;
  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether the button is in loading state
   * @default false
   */
  loading?: boolean;
  /**
   * Whether to show "Loading label" text when loading
   * @default false
   */
  loadingLabel?: boolean;
  /**
   * Icon to display (ReactNode)
   */
  icon?: React.ReactNode;
  /**
   * Icon position
   * @default "end"
   */
  iconPosition?: ButtonIconPosition;
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Button type attribute
   * @default "button"
   */
  type?: "button" | "submit" | "reset";

  /**
   * Optional inline styles
   */
  style?: React.CSSProperties;
  /**
   * Accessible label (for icon-only buttons)
   */
  "aria-label"?: string;
  /**
   * For combobox / menu trigger patterns (e.g. split buttons with a dropdown)
   */
  "aria-expanded"?: boolean;
  "aria-haspopup"?:
    | true
    | "menu"
    | "dialog"
    | "listbox"
    | "tree"
    | "grid"
    | "false";
  "aria-controls"?: string;
  id?: string;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

// Size to pixel mapping (from Figma)
const sizeMap: Record<ButtonSize, { height: number; paddingX: number }> = {
  xSmall: { height: 28, paddingX: 12 },
  small: { height: 40, paddingX: 24 }, // px-6 = 24px
  medium: { height: 48, paddingX: 24 }, // px-6 = 24px
};

// Icon size (from Figma)
const iconSize = 16;

// Spinner size mapping
const spinnerSizeMap: Record<ButtonSize, "small" | "medium"> = {
  xSmall: "small",
  small: "small",
  medium: "medium",
};

/**
 * Button - FauxDS (recreated from Figma MCP)
 *
 * Buttons help users take action or make a choice across a hierarchy of emphasis.
 * This implementation was created by extracting design specs directly from Figma.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      destructive = false,
      size = "medium",
      disabled = false,
      loading = false,
      loadingLabel = false,
      icon,
      iconPosition = "end",
      onClick,
      type = "button",
      className = "",
      style,
      "aria-label": ariaLabel,
      "aria-expanded": ariaExpanded,
      "aria-haspopup": ariaHaspopup,
      "aria-controls": ariaControls,
      id: buttonId,
    },
    ref
  ) => {
    const sizeStyles = sizeMap[size];
    const spinnerSize = spinnerSizeMap[size];

    // Determine visual state styles based on Figma design tokens
    const getVariantStyles = () => {
      if (disabled) {
        return {
          bg: "bg-[var(--slate-color-action-surface-disabled)]", // #f7f7f7
          border:
            variant === "secondary"
              ? "border-[var(--slate-color-action-border-disabled)]"
              : "border-transparent",
          text: "text-[var(--slate-color-action-text-disabled)]", // #757575
          hoverBg: "",
          activeBg: "",
          hoverBorder: "",
          activeBorder: "",
          hoverText: "",
          activeText: "",
        };
      }

      // Destructive styles use critical colors
      if (destructive) {
        switch (variant) {
          case "primary":
            return {
              bg: "bg-[var(--slate-color-red-100)]", // #f2e5e1 (light pink)
              border: "border-[var(--slate-color-action-border-critical)]", // #d17e70 (red border)
              text: "text-[var(--slate-color-red-700)]", // #921100 (dark red)
              hoverBg: "hover:bg-[var(--slate-color-red-700)]", // #921100 (dark red)
              activeBg: "active:bg-[var(--slate-color-red-700)]", // #921100
              hoverBorder: "hover:border-[var(--slate-color-red-700)]", // #921100
              activeBorder: "active:border-[var(--slate-color-red-700)]", // #921100
              hoverText: "hover:text-[var(--slate-color-grey-100)]", // #ffffff (white)
              activeText: "active:text-[var(--slate-color-grey-100)]", // #ffffff
            };
          case "secondary":
            return {
              bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
              border: "border-[var(--slate-color-action-border-critical)]", // #d17e70
              text: "text-[var(--slate-color-red-700)]", // #921100
              hoverBg:
                "hover:bg-[var(--slate-color-action-surface-inverse-hover)]", // #f7f7f7
              activeBg:
                "active:bg-[var(--slate-color-action-surface-inverse-active)]", // #f7f7f7
              hoverBorder: "hover:border-[var(--slate-color-red-700)]", // #921100
              activeBorder: "active:border-[var(--slate-color-red-700)]", // #921100
              hoverText: "hover:text-[var(--slate-color-red-800)]", // #5a1e09
              activeText: "active:text-[var(--slate-color-red-800)]", // #5a1e09
            };
          case "tertiary":
            return {
              bg: "bg-transparent",
              border: "border-transparent",
              text: "text-[var(--slate-color-red-700)]", // #921100
              hoverBg:
                "hover:bg-[var(--slate-color-action-surface-inverse-hover)]", // #f7f7f7
              activeBg:
                "active:bg-[var(--slate-color-action-surface-inverse-active)]", // #f7f7f7
              hoverBorder: "",
              activeBorder: "",
              hoverText: "hover:text-[var(--slate-color-red-800)]", // #5a1e09
              activeText: "active:text-[var(--slate-color-red-800)]", // #5a1e09
            };
          case "plain":
            return {
              bg: "bg-transparent",
              border: "border-transparent",
              text: "text-[var(--slate-color-red-700)] underline underline-offset-[0.25em]", // #921100
              hoverBg: "",
              activeBg: "",
              hoverBorder: "",
              activeBorder: "",
              hoverText: "hover:text-[var(--slate-color-red-800)]", // #5a1e09
              activeText: "active:text-[var(--slate-color-red-800)]", // #5a1e09
            };
        }
      }

      // Non-destructive styles
      switch (variant) {
        case "primary":
          return {
            bg: "bg-[var(--slate-color-action-surface-default)]", // #333333
            border: "border-transparent",
            text: "text-[var(--slate-color-action-text-inverse)]", // #ffffff
            hoverBg: "hover:bg-[var(--slate-color-action-surface-hover)]", // #000000
            activeBg: "active:bg-[var(--slate-color-action-surface-active)]", // #000000
            hoverBorder: "",
            activeBorder: "",
            hoverText: "",
            activeText: "",
          };
        case "secondary":
          return {
            bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
            border: "border-[var(--slate-color-action-border-subdued)]", // #dfe0e1
            text: "text-[var(--slate-color-action-text-default)]", // #333333
            hoverBg:
              "hover:bg-[var(--slate-color-action-surface-inverse-hover)]", // #f7f7f7
            activeBg:
              "active:bg-[var(--slate-color-action-surface-inverse-active)]", // #f7f7f7
            hoverBorder:
              "hover:border-[var(--slate-color-action-border-hover)]", // #000000
            activeBorder:
              "active:border-[var(--slate-color-action-border-active)]", // #000000
            hoverText: "hover:text-[var(--slate-color-action-text-hover)]", // #000000
            activeText: "active:text-[var(--slate-color-action-text-active)]", // #000000
          };
        case "tertiary":
          return {
            bg: "bg-transparent",
            border: "border-transparent",
            text: "text-[var(--slate-color-action-text-default)]", // #333333
            hoverBg:
              "hover:bg-[var(--slate-color-action-surface-inverse-hover)]", // #f7f7f7
            activeBg:
              "active:bg-[var(--slate-color-action-surface-inverse-active)]", // #f7f7f7
            hoverBorder: "",
            activeBorder: "",
            hoverText: "hover:text-[var(--slate-color-action-text-hover)]", // #000000
            activeText: "active:text-[var(--slate-color-action-text-active)]", // #000000
          };
        case "plain":
          return {
            bg: "bg-transparent",
            border: "border-transparent",
            text: "text-[var(--slate-color-action-text-default)] underline underline-offset-[0.25em]", // #333333
            hoverBg: "",
            activeBg: "",
            hoverBorder: "",
            activeBorder: "",
            hoverText: "hover:text-[var(--slate-color-action-text-hover)]", // #000000
            activeText: "active:text-[var(--slate-color-action-text-active)]", // #000000
          };
      }
    };

    const variantStyles = getVariantStyles();

    // Plain variant has different sizing (auto height, no padding)
    const isPlain = variant === "plain";
    const heightStyle = isPlain ? "h-auto" : `h-[${sizeStyles.height}px]`;
    const paddingXStyle = isPlain ? "px-0" : `px-[${sizeStyles.paddingX}px]`;

    // Focus ring styles
    const focusRingStyles =
      "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)]";

    // Loading text
    const displayText = loading && loadingLabel ? "Loading label" : children;

    // Icon color should match text color
    const iconColorClass = variantStyles.text;

    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        aria-label={ariaLabel}
        aria-busy={loading}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHaspopup}
        aria-controls={ariaControls}
        id={buttonId}
        className={cn(
          "inline-flex cursor-pointer items-center justify-center gap-2 rounded-[var(--slate-radius-component-default)] border font-sans transition-colors duration-150 ease-in-out",
          focusRingStyles,
          "disabled:cursor-not-allowed",
          heightStyle,
          paddingXStyle,
          variantStyles.bg,
          variantStyles.border,
          variantStyles.text,
          !disabled && !loading ? variantStyles.hoverBg : "",
          !disabled && !loading ? variantStyles.activeBg : "",
          !disabled && !loading ? variantStyles.hoverBorder : "",
          !disabled && !loading ? variantStyles.activeBorder : "",
          !disabled && !loading ? variantStyles.hoverText : "",
          !disabled && !loading ? variantStyles.activeText : "",
          isPlain ? "inline" : "",
          className
        )}
        style={{
          fontFamily: "var(--slate-typography-family-sans)",
          fontSize:
            size === "xSmall"
              ? "var(--slate-typography-font-size-100)"
              : "var(--slate-typography-font-size-200)",
          fontWeight: "var(--slate-typography-weight-regular)",
          lineHeight:
            size === "xSmall"
              ? "var(--slate-typography-line-height-100)"
              : "var(--slate-typography-line-height-200)",
          letterSpacing:
            size === "xSmall"
              ? "var(--slate-typography-letter-spacing-100)"
              : "var(--slate-typography-letter-spacing-200)",
          ...(variant === "tertiary"
            ? { mixBlendMode: "multiply" as const }
            : {}),
          ...style,
        }}
      >
        {/* Icon at start */}
        {icon && iconPosition === "start" && !loading && (
          <span
            className="flex shrink-0 items-center justify-center"
            style={{ width: iconSize, height: iconSize }}
          >
            <span className={iconColorClass}>{icon}</span>
          </span>
        )}

        {/* Loading spinner */}
        {loading && (
          <span className="[&_svg]:text-inherit!">
            <LoadingSpinner size={spinnerSize} aria-label="Loading" />
          </span>
        )}

        {/* Text content */}
        {displayText && (
          <span className={isPlain ? "inline" : "line-clamp-2 text-center"}>
            {displayText}
          </span>
        )}

        {/* Icon at end */}
        {icon && iconPosition === "end" && !loading && (
          <span
            className="flex shrink-0 items-center justify-center"
            style={{ width: iconSize, height: iconSize }}
          >
            <span className={iconColorClass}>{icon}</span>
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
